import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';
import { isSorSubTask, readTaskGate, Task, TaskGate, TaskStatus } from '@jms/model';
import { AvatarComponent } from '@workbuddy/ui/components/avatar';

/** View model for one row of the card (subset of Task the UI needs). */
interface TaskCardRow {
  id: string;
  title: string;
  mandatory: boolean;
  assigneeLabel: string | null;
  checkIcon: string;
}

/**
 * Tasks hover-card content for the board's task counter (Figma Jobs 1747:35991): each child
 * task with its completion check, a mandatory marker and the assignee.
 *
 * Content only — the dark surface comes from the shared `wb-dark-tooltip` class, since the card is
 * rendered through a PrimeNG tooltip appended to body.
 */
@Component({
  selector: 'item-tasks-card',
  template: `
    <div class="tasks-card">
      <div class="card-title">{{ $title() }}</div>
      <ul class="card-list">
        @for (row of $rows(); track row.id) {
          <li class="card-row">
            <div class="card-main">
              <i [class]="row.checkIcon"></i>
              <span class="card-name">{{ row.title }}</span>
              @if (row.mandatory) {
                <span class="card-required" aria-label="Mandatory">*</span>
              }
            </div>
            <div class="card-assignee" [class.unassigned]="!row.assigneeLabel">
              <w-avatar [value]="row.assigneeLabel || '?'" [size]="16" />
              <span class="card-assignee-name">{{ row.assigneeLabel || 'Unassigned' }}</span>
            </div>
          </li>
        }
      </ul>
    </div>
  `,
  styleUrls: ['./item.tasks.card.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent],
})
export class ItemTasksCardComponent {

  public readonly $tasks = input<Task[]>([], { alias: 'tasks' });
  public readonly $title = input('SOR Tasks', { alias: 'title' });

  /** SOR sub-tasks only, the same set the detail panel lists under "SOR's Tasks". */
  protected readonly $rows = computed<TaskCardRow[]>(() => this.$tasks()
    .filter(isSorSubTask)
    .map(toCardRow));
}

function toCardRow(task: Task): TaskCardRow {

  const done = task.status === TaskStatus.Completed;

  return {
    id: task._id,
    title: task.name ?? task.description ?? '',
    mandatory: readTaskGate(task) !== TaskGate.NotEnforced,
    assigneeLabel: task.assignee?.name ? shortName(task.assignee.name) : null,
    checkIcon: done ? 'fa-solid fa-circle-check card-check done' : 'fa-regular fa-circle-dashed card-check',
  };
}

/** "Sarah Lambert" → "Sarah L." — the card's meta column is narrow (prototype shortName). */
function shortName(name: string): string {

  const parts = name.trim().split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;

  return last ? `${parts[0]} ${last[0]}.` : parts[0];
}
